import * as React from 'react'

import { useRoles } from '@/core/hooks/account/useRoles'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { cn } from '@/shared/utils/helpers'

import { getRoleDisplayName } from './Users.utils'

type RoleSelectorProps = {
	value?: number | string
	onChange: (roleId: number | string) => void
	placeholder?: string
	disabled?: boolean
	className?: string
}

export const RoleSelector: React.FC<RoleSelectorProps> = ({
	value: selectedRoleId,
	onChange,
	placeholder = 'Select role...',
	disabled = false,
	className,
}) => {
	const { data: roles = [], isLoading, isError } = useRoles()

	const selectedRole = React.useMemo(() => {
		if (!selectedRoleId || roles.length === 0) return null
		// Compare user.roleId (selectedRoleId) with role.id - ensure numeric comparison
		const roleIdNum =
			typeof selectedRoleId === 'string'
				? parseInt(selectedRoleId, 10)
				: selectedRoleId

		return roles.find((role) => role.id === roleIdNum) || null
	}, [selectedRoleId, roles])

	// Convert value to string for Select component (Radix UI Select uses strings)
	const selectValue = React.useMemo(() => {
		if (!selectedRoleId || selectedRoleId === 0) return undefined
		// Ensure we convert to string for Select component
		const roleIdNum =
			typeof selectedRoleId === 'string'
				? parseInt(selectedRoleId, 10)
				: selectedRoleId
		return roleIdNum.toString()
	}, [selectedRoleId])

	if (isError) {
		return (
			<div className="flex items-center justify-center h-12 px-3 text-sm text-destructive bg-card border border-destructive rounded-lg">
				Error loading roles
			</div>
		)
	}

	return (
		<Select
			value={selectValue}
			onValueChange={(value) => onChange(value)}
			disabled={disabled || isLoading}
		>
			<SelectTrigger className={cn('w-full', className)}>
				<SelectValue placeholder={isLoading ? 'Loading...' : placeholder}>
					{selectedRole?.public_name
						? getRoleDisplayName(selectedRole.public_name)
						: null}
				</SelectValue>
			</SelectTrigger>
			<SelectContent className="z-[9999]" style={{ zIndex: 9999 }}>
				{roles.map((role) => (
					<SelectItem
						key={role.id}
						value={role.id.toString()}
						className="text-left max-w-[300px]"
					>
						<div className="flex flex-col items-start">
							<span className="font-medium">
								{getRoleDisplayName(role.public_name)}
							</span>
							{role.description && (
								<span className="text-xs text-muted-foreground">
									{role.description}
								</span>
							)}
						</div>
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}
